import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MEMBERSHIP_ROLE_LABELS, hasRoleAtLeast, type MembershipRole } from '@raro/domain';
import { Glyph, Avatar, Button, Label, Notice, Select, Tag, Title } from '@raro/ui';
import { useState } from 'react';
import { InviteForm } from '../../components/InviteForm';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate } from '../../lib/format';

interface TeamRow { membershipId: string; role: MembershipRole; user: { id: string; name: string; email: string; title: string | null; isActive: boolean; lastLoginAt: string | null; company: string | null; specialty: string | null } }

export function PessoasPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const team = useQuery({ queryKey: ['raro-team'], queryFn: () => api.get<TeamRow[]>('/people/raro') });
  const home = me?.memberships.find((m) => m.organization.isHome)?.organization;
  const canManage = hasRoleAtLeast(me?.internalRole ?? 'client', 'partner');
  const [resetLink, setResetLink] = useState<string | null>(null);
  const reset = useMutation({ mutationFn: (userId: string) => api.post<{ link: string }>(`/people/${userId}/reset-link`), onSuccess: (d) => setResetLink(d.link) });
  const changeRole = useMutation({ mutationFn: ({ membershipId, role }: { membershipId: string; role: MembershipRole }) => api.patch(`/organizations/${home!.id}/members/${membershipId}`, { role }), onSuccess: () => qc.invalidateQueries({ queryKey: ['raro-team'] }) });
  return (
    <div className="rr-page rr-stack rr-stack--7">
      <header className="rr-stack rr-stack--2"><Glyph name="perfil" size={64} className="rr-page-glyph" /><Label>Pessoas</Label><Title>quem faz a Raro.</Title><p className="rr-muted">sócios, equipe, produção e parceiros. pessoas do cliente entram pela página de cada cliente.</p></header>
      <section className="rr-section">
        <Label>Equipe</Label>
        <div className="rr-list">
          {team.data?.map((m) => (
            <div key={m.membershipId} className="rr-list-row" style={{ gridTemplateColumns: '36px 1fr auto' }}>
              <Avatar name={m.user.name} accent={m.user.id === me?.id} />
              <div><div className="rr-list-row__title">{m.user.name} <span className="rr-faint">· {m.user.email}</span></div><div className="rr-list-row__meta">{m.user.title && <span>{m.user.title}</span>}{m.user.lastLoginAt && <span>último acesso {formatDate(m.user.lastLoginAt)}</span>}{!m.user.isActive && <span className="rr-list-row__err">inativo</span>}</div></div>
              <div className="rr-row">
                {canManage && m.user.id !== me?.id ? (
                  <Select style={{ width: 'auto', height: 30, fontSize: 'var(--rr-text-xs)' }} value={m.role} onChange={(e) => changeRole.mutate({ membershipId: m.membershipId, role: e.target.value as MembershipRole })}>
                    {(['owner', 'partner', 'analyst', 'maker'] as MembershipRole[]).map((r) => <option key={r} value={r}>{MEMBERSHIP_ROLE_LABELS[r]}</option>)}
                  </Select>
                ) : <Tag tone={m.role === 'owner' || m.role === 'partner' ? 'accent' : 'outline'}>{MEMBERSHIP_ROLE_LABELS[m.role]}</Tag>}
                {canManage && m.user.id !== me?.id && <Button size="sm" variant="quiet" onClick={() => reset.mutate(m.user.id)}>link de senha</Button>}
              </div>
            </div>
          ))}
        </div>
        {resetLink && <Notice>link de redefinição (sem e-mail configurado, envie à mão): <code style={{ userSelect: 'all', fontSize: 'var(--rr-text-xs)' }}>{resetLink}</code></Notice>}
      </section>
      {home && canManage && <InviteForm organizationId={home.id} allowedRoles={['partner', 'analyst', 'maker']} title="Convidar para a Raro" />}
    </div>
  );
}
